import { useState, useEffect } from 'react';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Typography } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, RemoveRedEye, KeyboardArrowLeft, Add as AddIcon } from '@mui/icons-material';
import Tab from '@mui/material/Tab';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import { getRequest, postRequest, showMessage } from '../../../../config';
import { useDispatch, useSelector } from 'react-redux';
import { toggleModal } from '../../../../reducers/modalReducer';
import CreateDiscountCoupon from '../CreateDiscountCoupon';
import DeleteCouponModal from '../../../../components/modals/deleteCoupon';
import CreateSubscriptionPlan from '../../../../components/modals/CreateSubscriptionPlan.jsx';
import DeleteSubscriptionPlan from '../../../../components/modals/DeleteSubscriptionPlan.jsx';
import moment from 'moment/moment';

const TransactionSettings = () => {
    const [coupons, setCoupons] = useState([]);
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [completeData, setCompleteData] = useState(null);
    const [step, setStep] = useState('coupons');
    const [tab, setTab] = useState('coupons');
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const modal = useSelector(state => state.modal);

    useEffect(() => {
        fetchCoupons();
        fetchSubscriptionPlans();
    }, []);

    const handleEdit = (coupon) => {
        setSelectedCoupon(coupon);
        dispatch(toggleModal({ open: 'createCoupon' }));
    };

    const handleView = (coupon) => {
        handleFetchCoupon(coupon?.code);
        setSelectedCoupon(coupon);
        setStep('viewCoupon');
    };

    const fetchCoupons = async () => {
        try {
            const response = await getRequest('/payment/getCoupons');
            setCoupons(response.body.coupons);
        } catch (error) {
            console.error('Error fetching coupons:', error);
        } finally {
            setSelectedCoupon(null);
        }
    };

    const handleFetchCoupon = async (code) => {
        setLoading(true);
        const response = await getRequest(`/payment/getCoupon/${code}`);
        if (response.code === 200) {
            setCompleteData(response.body);
        } else {
            showMessage('Coupon not found');
        }
        setLoading(false);
    }

    const handleDelete = async (coupon) => {
        console.log('Deleting coupon:', coupon);
        if (!coupon) {
            showMessage('No coupon selected for deletion');
            return;
        }
        if (!coupon._id) {
            showMessage('Coupon ID is missing:', coupon);
            return;
        }
        setLoading(true);
        try {
            await postRequest(`/payment/deleteCoupon/${coupon._id}`, {});
            fetchCoupons();
        } catch (error) {
            console.error('Error deleting coupon:', error);
        } finally {
            setLoading(false);
            dispatch(toggleModal({ open: '' }));
            setSelectedCoupon(null);
        }
    };

    const fetchSubscriptionPlans = async () => {
        try {
            const response = await getRequest('/payment/subscription-plans');
            setSubscriptionPlans(response.body.plans || []);
        } catch (error) {
            console.error('Error fetching subscription plans:', error);
            // For now, show sample data if API fails
            setSubscriptionPlans([
                { _id: '1', name: 'Lite', price: 99, currency: 'gbp', priceId: 'price_lite_123', isActive: true },
                { _id: '2', name: 'Pro', price: 999, currency: 'gbp', priceId: 'price_pro_456', isActive: true }
            ]);
        }
    };

    const handleEditPlan = (plan) => {
        setSelectedPlan(plan);
        dispatch(toggleModal({ open: 'createSubscriptionPlan' }));
    };

    const handleDeletePlan = async (plan) => {
        console.log('Deleting plan:', plan);
        if (!plan) {
            showMessage('No plan selected for deletion');
            return;
        }
        if (!plan._id) {
            showMessage('Plan ID is missing:', plan);
            return;
        }
        setLoading(true);
        try {
            await postRequest(`/payment/delete-subscription-plan/${plan._id}`, {});
            fetchSubscriptionPlans();
        } catch (error) {
            console.error('Error deleting plan:', error);
        } finally {
            setLoading(false);
            dispatch(toggleModal({ open: '' }));
            setSelectedPlan(null);
        }
    };

    const renderCouponsTab = () => {
        if (step === 'viewCoupon') {
            return (
                <Box sx={{ p: 3 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setStep('coupons');
                            setSelectedCoupon(null);
                            dispatch(toggleModal({ open: '' }))
                            setCompleteData(null);
                        }}
                    >
                        <KeyboardArrowLeft /> Back
                    </Button>
                    <Typography variant="h6" className='tac c15 mt1rem mb2rem'>Coupon Code: {selectedCoupon?.code}</Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Coupon Code</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Purpose</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Company</TableCell>
                                    <TableCell>Transaction Date</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? <TableRow>
                                    <TableCell colSpan={7}>
                                        <div className="df aic jcc w100 h100">
                                            <h1 className='tac c15 fs1-75rem pt1rem py3rem lh2'>Loading...</h1>
                                        </div>
                                    </TableCell>
                                </TableRow> : completeData && completeData?.payments && completeData?.payments.length ? completeData?.payments.map((payment) => (
                                    <TableRow key={payment._id}>
                                        <TableCell>{completeData?.coupon?.code}</TableCell>
                                        <TableCell className='ttc'>{payment.amount}</TableCell>
                                        <TableCell className='ttc'>{payment.purpose.includes('=') ? `${payment.purpose.split('=')[0].split('-')[1]} - ${payment.purpose.split('=')[1].includes('HealthBoost') ? payment.purpose.split('=')[1].split('-')[1] : payment.purpose.split('=')[1]}` : payment.purpose}</TableCell>
                                        <TableCell>
                                            {payment?.userId?.firstName} {payment?.userId?.lastName ? payment?.userId?.lastName : ""}
                                        </TableCell>
                                        <TableCell>{payment?.companyId?.cName}</TableCell>
                                        <TableCell>{moment(payment.createdAt).format('DD-MM-YYYY HH:mm A')}</TableCell>
                                        <TableCell>
                                            {payment.status}
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={7}>
                                            <div className='mih50vh w100 df aic jcc'>No Transactions</div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            );
        }

        return (
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedCoupon(null);
                            dispatch(toggleModal({ open: 'createCoupon' }))
                        }}
                    >
                        Create New Coupon
                    </Button>
                </Box>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Coupon Code</TableCell>
                                <TableCell>Discount Type</TableCell>
                                <TableCell>Discount Value</TableCell>
                                <TableCell>Valid From</TableCell>
                                <TableCell>Valid Until</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {coupons.map((coupon) => (
                                <TableRow key={coupon._id}>
                                    <TableCell>{coupon.code}</TableCell>
                                    <TableCell className='ttc'>{coupon.discountType}</TableCell>
                                    <TableCell>
                                        {coupon.discountType === 'percentage' ? (
                                            <div className='df aic'>
                                                {coupon.discountValue}%{' '}
                                                {coupon.discountValue === 100 && (
                                                    <img src={require('../../../../../images/checklist-logo.webp')} alt="dglogo" className='ml0-5rem' width={20} height={16} />
                                                )}
                                            </div>
                                        ) : (
                                            <>£{coupon.discountValue}</>
                                        )}
                                    </TableCell>
                                    <TableCell>{new Date(coupon.validFrom).toLocaleDateString()}</TableCell>
                                    <TableCell>{new Date(coupon.expiresAt).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <Typography
                                            color={coupon.isActive ? 'success.main' : 'error.main'}
                                        >
                                            {coupon.isActive ? 'Active' : 'Inactive'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleView(coupon)}
                                        >
                                            <RemoveRedEye />
                                        </IconButton>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleEdit(coupon)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => {
                                                dispatch(toggleModal({ open: 'deleteCoupon' }))
                                                setSelectedCoupon(coupon);
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    const renderSubscriptionPlansTab = () => {
        return (
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedPlan(null);
                            dispatch(toggleModal({ open: 'createSubscriptionPlan' }))
                        }}
                    >
                        <AddIcon /> Create New Plan
                    </Button>
                </Box>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Plan Name</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Currency</TableCell>
                                <TableCell>Stripe Price ID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subscriptionPlans.map((plan) => (
                                <TableRow key={plan._id || plan.name}>
                                    <TableCell>{plan.name}</TableCell>
                                    <TableCell>£{plan.price}</TableCell>
                                    <TableCell className='ttc'>{plan.currency}</TableCell>
                                    <TableCell>{plan.priceId}</TableCell>
                                    <TableCell>
                                        <Typography
                                            color={plan.isActive !== false ? 'success.main' : 'error.main'}
                                        >
                                            {plan.isActive !== false ? 'Active' : 'Inactive'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleEditPlan(plan)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => {
                                                dispatch(toggleModal({ open: 'deleteSubscriptionPlan' }))
                                                setSelectedPlan(plan);
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return (
        <>
            <TabContext value={tab}>
                <TabList onChange={(e, value) => setTab(value)}>
                    <Tab className="fs1rem fw600" label='Coupons' value='coupons' />
                    <Tab className="fs1rem fw600" label='Subscription Plans' value='subscription-plans' />
                </TabList>
                <TabPanel className='w100' value='coupons'>
                    {renderCouponsTab()}
                </TabPanel>
                <TabPanel className='w100' value='subscription-plans'>
                    {renderSubscriptionPlansTab()}
                </TabPanel>
            </TabContext>

            <CreateDiscountCoupon selectedCoupon={selectedCoupon} fetchCoupons={fetchCoupons} />
            <DeleteCouponModal
                deleteCoupon={selectedCoupon}
                onConfirm={() => {
                    handleDelete(selectedCoupon);
                    setSelectedCoupon(null);
                }}
                loading={loading}
                onCancel={() => setSelectedCoupon(null)}
                modalType="deleteCoupon"
            />

            <CreateSubscriptionPlan
                selectedPlan={selectedPlan}
                fetchPlans={fetchSubscriptionPlans}
                modalType={modal.open}
            />
            <DeleteSubscriptionPlan
                deletePlan={selectedPlan}
                onConfirm={() => {
                    handleDeletePlan(selectedPlan);
                    setSelectedPlan(null);
                }}
                loading={loading}
                onCancel={() => {
                    setSelectedPlan(null);
                    dispatch(toggleModal({ open: '' }));
                }}
                modalType={modal.open}
            />
        </>
    );
};

export default TransactionSettings;